import React, { useState, useEffect } from "react";
import {
  fetchVacationDaysForYear,
  submitHolidayRequest,
  fetchUserHolidayRequests,
} from "./api";
import { toast } from "react-toastify";

// ---- helper pour formater les erreurs API (axios ou fetch) ----
function formatApiError(err, fallback = "Failed to submit holiday request.") {
  const data   = err?.response?.data || err?.data || null;   // axios ou fetch wrapper
  const status = err?.response?.status || err?.status || null;

  // message simple si présent
  let msg = (data && (data.message || data.error)) || err?.message || fallback;

  // cas détaillé: overdraft côté serveur
  if (data?.error === "OVERDRAFT_EXCEEDED") {
    const allowed = data.allowedOverdraftHours ?? 30;
    // détails par année si fournis
    if (data.missingByYear && typeof data.missingByYear === "object") {
      const parts = Object.entries(data.missingByYear)
        .map(([year, h]) => `${year}: ${h}h`)
        .join(", ");
      msg = `This request would exceed the allowed overdraft (${allowed}h). Shortfall: ${parts}.`;
    } else if (typeof data.missingHours === "number") {
      msg = `This request would exceed the allowed overdraft (${allowed}h) by ${data.missingHours}h.`;
    } else if (data.message) {
      msg = data.message;
    }
  }

  // fallback enrichi si on a juste le statut
  if (!data && status) msg = `${fallback} (HTTP ${status})`;

  return msg;
}


function HolidaysRequest() {
  const thisYear = new Date().getFullYear();

  const [selectedYear, setSelectedYear] = useState(thisYear);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Remaining for selected year
  const [remainingHours, setRemainingHours] = useState(0);

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [startDayType, setStartDayType] = useState("full"); // full, morning, afternoon
  const [endDayType, setEndDayType] = useState("full"); // full, morning, afternoon

  // ---- Helpers ----
  const clampToSelectedYear = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.getFullYear() === Number(selectedYear) ? iso : "";
  };

  // Load remaining for the selected year
  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await fetchVacationDaysForYear(selectedYear);
        // backend returns: { year, entitlement_hours, carried_over_hours, adjustments_hours, taken_hours, remaining_hours, vacation_hours(back-compat) }
        setRemainingHours(Number(data.remaining_hours ?? 0));
      } catch {
        setRemainingHours(0);
        toast.error("Failed to fetch vacation balance for the selected year.");
      }
    };
    run();
    // When the year changes, clear dates that don't belong to it
    setStartDate((prev) => clampToSelectedYear(prev));
    setEndDate((prev) => clampToSelectedYear(prev));
    setMessage("");
  }, [selectedYear]);

  // ---- Business rules ----

  // Block 24–31 December (same rule you had)
  function isDateInRestrictedRange(date) {
    const year = date.getFullYear();
    const restrictedStart = new Date(year, 11, 24); // Dec 24
    const restrictedEnd = new Date(year, 11, 31); // Dec 31
    restrictedStart.setHours(0, 0, 0, 0);
    restrictedEnd.setHours(23, 59, 59, 999);
    const d = new Date(date);
    d.setHours(12, 0, 0, 0);
    return d >= restrictedStart && d <= restrictedEnd;
  }

  // Ranges overlap?
  const rangesOverlap = (start1, end1, start2, end2) =>
    new Date(start1) <= new Date(end2) && new Date(start2) <= new Date(end1);

  // ---- Handlers ----
  const handleStartDateChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const d = new Date(val);

    if (d.getFullYear() !== Number(selectedYear)) {
      toast.error(`Start date must be within ${selectedYear}.`);
      return;
    }
    if (isDateInRestrictedRange(d)) {
      toast.error(
        "Start date cannot be between December 24th and 31st inclusive."
      );
      return;
    }

    setStartDate(val);

    // auto-fix end date if needed
    if (!endDate || new Date(val) > new Date(endDate)) {
      setEndDate(val);
      setMessage("");
    }
  };

  const handleEndDateChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const d = new Date(val);

    if (d.getFullYear() !== Number(selectedYear)) {
      toast.error(`End date must be within ${selectedYear}.`);
      return;
    }
    if (isDateInRestrictedRange(d)) {
      toast.error(
        "End date cannot be between December 24th and 31st inclusive."
      );
      return;
    }

    if (startDate && new Date(val) < new Date(startDate)) {
      setStartDate(val);
      setMessage("");
    }
    setEndDate(val);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      if (!startDate || !endDate) {
        toast.error("Please choose start and end dates.");
        setLoading(false);
        return;
      }

      const s = new Date(startDate);
      const eDate = new Date(endDate);

      // 1) Single calendar year only
      if (
        s.getFullYear() !== eDate.getFullYear() ||
        s.getFullYear() !== Number(selectedYear)
      ) {
        toast.error(
          "Your request must be contained within one calendar year. Please submit two separate requests."
        );
        setLoading(false);
        return;
      }

      /* 2) Minimum remaining rule (legacy rule you had)
      if (remainingHours < -30) {
        const msg =
          "Insufficient vacation hours: you have less than -30 hours available for this year.";
        setMessage(msg);
        toast.error(msg);
        setLoading(false);
        return;
      }*/
	// 2) Overdraft is validated by the backend in server.js.
	// Do not block here, because user-specific limits are handled server-side.

      // 3) Overlap check (same year only; ignore cancelled/rejected)
      const { data: userReqs } = await fetchUserHolidayRequests();
      const overlapping = userReqs.find((r) => {
        if (["cancelled", "rejected"].includes(r.status)) return false;
        const sameYear =
          new Date(r.start_date).getFullYear() === Number(selectedYear) ||
          new Date(r.end_date).getFullYear() === Number(selectedYear);
        if (!sameYear) return false;
        return rangesOverlap(r.start_date, r.end_date, startDate, endDate);
      });
      if (overlapping) {
        const msg =
          "You already have a holiday request overlapping the selected dates.";
        setMessage(msg);
        toast.error(msg);
        setLoading(false);
        return;
      }

      // Submit request (backend will account for bank holidays correctly)
      await submitHolidayRequest(startDate, endDate, startDayType, endDayType);

      setMessage("Holiday request submitted successfully.");
      toast.success("Holiday request submitted successfully.");

      // optional: reset form
      setStartDate("");
      setEndDate("");
      setStartDayType("full");
      setEndDayType("full");

      // refresh remaining for the year (it won't actually deduct until admin approves,
      // but this keeps UX consistent if you later change the behavior)
      const { data } = await fetchVacationDaysForYear(selectedYear);
      setRemainingHours(Number(data.remaining_hours ?? 0));
    } catch (err) {
  const msg = formatApiError(err, "Failed to submit holiday request.");
  setMessage(msg);
  toast.error(msg);
} finally {
  setLoading(false);
}
  };

  return (
    <div className="flex items-center justify-center bg-gray-100 py-12 px-4">
      <div className="w-full max-w-md bg-white p-8 rounded shadow-md">
        <h1 className="text-3xl font-semibold mb-6 text-center">
          Request Holidays
        </h1>

        {/* Year selector */}
        <div className="mb-6 flex items-center justify-between">
          <label className="text-gray-700 font-medium">Year:</label>
          <input
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="w-28 px-3 py-2 border rounded"
          />
        </div>

        <form onSubmit={handleSubmit}>
          <p className="mb-4 p-4 bg-gold text-white text-lg font-bold rounded text-center">
            Please note: The start date is the first day of your holiday, and
            the end date is included as the last day of your holiday.
          </p>

          {/* Start */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Start Date:
            </label>
            <input
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              className="w-full px-3 py-2 border rounded"
              required
            />
            <div className="mt-2 space-x-4">
              <label>
                <input
                  type="radio"
                  value="full"
                  checked={startDayType === "full"}
                  onChange={() => setStartDayType("full")}
                />{" "}
                Full Day
              </label>
              <label>
                <input
                  type="radio"
                  value="morning"
                  checked={startDayType === "morning"}
                  onChange={() => setStartDayType("morning")}
                />{" "}
                Morning
              </label>
              <label>
                <input
                  type="radio"
                  value="afternoon"
                  checked={startDayType === "afternoon"}
                  onChange={() => setStartDayType("afternoon")}
                />{" "}
                Afternoon
              </label>
            </div>
          </div>

          {/* End */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              End Date (included):
            </label>
            <input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              min={startDate || `${selectedYear}-01-01`}
              max={`${selectedYear}-12-31`}
              className="w-full px-3 py-2 border rounded"
              required
              disabled={!startDate}
            />
            <div className="mt-2 space-x-4">
              <label>
                <input
                  type="radio"
                  value="full"
                  checked={endDayType === "full"}
                  onChange={() => setEndDayType("full")}
                  disabled={!endDate}
                />{" "}
                Full Day
              </label>
              <label>
                <input
                  type="radio"
                  value="morning"
                  checked={endDayType === "morning"}
                  onChange={() => setEndDayType("morning")}
                  disabled={!endDate}
                />{" "}
                Morning
              </label>
              <label>
                <input
                  type="radio"
                  value="afternoon"
                  checked={endDayType === "afternoon"}
                  onChange={() => setEndDayType("afternoon")}
                  disabled={!endDate}
                />{" "}
                Afternoon
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-gold text-white py-2 px-4 rounded hover:opacity-85"
            disabled={loading || !startDate || !endDate}
          >
            {loading ? (
              <div className="flex justify-center items-center">
                <svg
                  className="animate-spin h-5 w-5 mr-3 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  ></path>
                </svg>
                Submitting...
              </div>
            ) : (
              "Submit Request"
            )}
          </button>
        </form>

        {message && <p className="mt-4 text-center text-red-500">{message}</p>}

        <div className="mt-4 text-center">
          <p className="text-gray-700">
            You have <strong>{remainingHours}</strong> hours remaining for{" "}
            <strong>{selectedYear}</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default HolidaysRequest;
